<template>
  <!-- Routed page -->
  <router-view v-if="isAppReady" />
  <!-- App-wide update notice -->
  <PwaUpdateNotice />
</template>

<script setup>
// Imports
import '/node_modules/flag-icons/css/flag-icons.min.css'

import { useEventListener, useMediaQuery } from '@vueuse/core'
import { format, getCssVar, setCssVar } from 'quasar'
import PwaUpdateNotice from 'src/components/PwaUpdateNotice.vue'

// Options
defineOptions({
  name: 'App',
})

// Stores
const authStore = useAuthStore()
const UIStore = useUIStore()
const { authResponse, whitelabelBrandingSettings } = storeToRefs(authStore)
const { sharingCenterMeeting, isPwaMode } = storeToRefs(UIStore)

// Constants
const { capitalize } = format
const { t, te, locale } = useI18n()
const route = useRoute()
const pwaDisplayModes = ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay']
const pwaDisplayModeMatches = pwaDisplayModes.map((mode) => useMediaQuery(`(display-mode: ${mode})`))

// Refs
const isAppReady = ref(process.env.FRONTEND_TYPE !== 'Customer')

// Computed
const resolvedSettings = computed(() => {
  const authResponseSettings = authResponse.value?.user?.resolvedSettings?.settings
  return Array.isArray(authResponseSettings) ? authResponseSettings : []
})

const customerBrandingSettings = computed(() => {
  return resolvedSettings.value.length > 0 ? resolvedSettings.value : whitelabelBrandingSettings.value
})

const meetingSettings = computed(() => {
  const settings = sharingCenterMeeting.value?.settings
  return settings && typeof settings === 'object' ? settings : null
})

const isSharingCenterRoute = computed(() => {
  const routeName = String(route?.name || '')
  return routeName.startsWith('sc-')
})

const portalName = computed(() => {
  const value =
    sharingCenterMeeting.value?.settings?.['portal-name'] ||
    resolvedSettings.value.find((setting) => setting?.key === 'portal-name')?.value ||
    whitelabelBrandingSettings.value.find((setting) => setting?.key === 'portal-name')?.value
  return typeof value === 'string' && value.length > 0 ? value : null
})

const sharingCenterProductName = computed(() => {
  const value =
    sharingCenterMeeting.value?.settings?.['sharingcenter-product-name'] ||
    resolvedSettings.value.find((setting) => setting?.key === 'sharingcenter-product-name')?.value
  return typeof value === 'string' && value.length > 0 ? value : null
})

const quirk = computed(() => {
  const routeName = String(route?.name || '')
  if (isSharingCenterRoute.value && sharingCenterProductName.value) {
    return sharingCenterProductName.value
  }

  const routeKey = isSharingCenterRoute.value ? 'sharing-center' : routeName
  return routeKey && te('Shared.Globals.Routes.' + routeKey) ? t('Shared.Globals.Routes.' + routeKey) : null
})

// Methods
const getResolvedSettingValue = (settings, key) => {
  if (typeof key !== 'string' || key.length === 0) {
    return null
  }

  if (Array.isArray(settings)) {
    const setting = settings.find((candidate) => {
      return candidate?.key === key
    })

    if (!setting || !Object.prototype.hasOwnProperty.call(setting, 'value')) {
      return null
    }

    return setting.value ?? null
  }

  if (settings && typeof settings === 'object') {
    if (!Object.prototype.hasOwnProperty.call(settings, key)) {
      return null
    }

    return settings[key] ?? null
  }

  return null
}

const isValidCssColor = (value) => {
  if (typeof document === 'undefined' || typeof value !== 'string') {
    return false
  }

  const probe = document.createElement('span')
  probe.style.color = ''
  probe.style.color = value
  return probe.style.color !== ''
}

const setThemeColorMeta = (value) => {
  if (typeof document === 'undefined' || !isValidCssColor(value)) {
    return
  }

  let metaThemeColor = document.querySelector('meta[name="theme-color"]')
  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta')
    metaThemeColor.setAttribute('name', 'theme-color')
    document.head.appendChild(metaThemeColor)
  }

  metaThemeColor.setAttribute('content', String(value).trim())
}

const getCurrentPrimaryColor = () => {
  if (typeof document === 'undefined') {
    return null
  }

  const cssVarPrimary = String(getCssVar('primary') || '').trim()
  if (isValidCssColor(cssVarPrimary)) {
    return cssVarPrimary
  }

  const computedPrimary = getComputedStyle(document.documentElement).getPropertyValue('--q-primary').trim()
  if (isValidCssColor(computedPrimary)) {
    return computedPrimary
  }

  return null
}

const applyPrimaryColorToTheme = (settings) => {
  if (typeof document === 'undefined') {
    return
  }

  if (process.env.FRONTEND_TYPE !== 'Customer') {
    const currentPrimary = getCurrentPrimaryColor()
    if (currentPrimary) {
      setThemeColorMeta(currentPrimary)
    }
    return
  }

  const configuredValue = getResolvedSettingValue(settings, 'primary-color')
  const normalizedValue = typeof configuredValue === 'string' ? configuredValue.trim() : ''
  const hasCustomPrimaryColor = isValidCssColor(normalizedValue)

  if (hasCustomPrimaryColor) {
    setCssVar('primary', normalizedValue)
    document.documentElement.style.setProperty('--primary-color', normalizedValue)
    setThemeColorMeta(normalizedValue)
    return
  }

  document.body?.style?.removeProperty('--q-primary')
  const defaultPrimaryColor = getCurrentPrimaryColor()
  if (defaultPrimaryColor) {
    document.documentElement.style.setProperty('--primary-color', defaultPrimaryColor)
    setThemeColorMeta(defaultPrimaryColor)
  } else {
    document.documentElement.style.removeProperty('--primary-color')
  }
}

const applyThemePrimaryColor = () => {
  const meetingPrimaryColor = getResolvedSettingValue(meetingSettings.value, 'primary-color')
  const hasMeetingPrimaryColor = typeof meetingPrimaryColor === 'string' && meetingPrimaryColor.trim().length > 0

  if (isSharingCenterRoute.value && hasMeetingPrimaryColor) {
    applyPrimaryColorToTheme(meetingSettings.value)
    return
  }

  applyPrimaryColorToTheme(customerBrandingSettings.value)
}

const hasPwaSourceQuery = () => {
  const source = route.query?.src
  return Array.isArray(source) ? source.includes('pwa') : source === 'pwa'
}

const detectPwaDisplayMode = () => {
  if (typeof window === 'undefined') {
    return false
  }

  if (typeof window.__OMNI_DETECT_PWA_DISPLAY_MODE__ === 'function') {
    return window.__OMNI_DETECT_PWA_DISPLAY_MODE__()
  }

  const hasMatchedDisplayMode = pwaDisplayModeMatches.some((match) => {
    return match.value === true
  })

  return hasMatchedDisplayMode || window.navigator?.standalone === true || document.referrer.startsWith('android-app://')
}

const isInstalledPwaDisplayMode = () => {
  if (typeof window === 'undefined') {
    return false
  }

  if (isPwaMode.value === true) {
    return true
  }

  if (window.__OMNI_IS_PWA_DISPLAY_MODE__ === true) {
    return true
  }

  const detected = detectPwaDisplayMode()
  if (detected) {
    window.__OMNI_IS_PWA_DISPLAY_MODE__ = true
  }

  return detected
}

const getDocumentTitle = () => {
  const productName = isInstalledPwaDisplayMode() ? null : portalName.value || process.env.PRODUCT_NAME
  const titleSuffix = quirk.value

  if (productName && titleSuffix) {
    return productName + ' - ' + titleSuffix
  }

  return productName || titleSuffix || ''
}

const setDocumentTitle = () => {
  if (typeof document === 'undefined') {
    return
  }

  document.title = getDocumentTitle()
}

const syncPwaDisplayModeTitle = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (hasPwaSourceQuery() || detectPwaDisplayMode()) {
    window.__OMNI_IS_PWA_DISPLAY_MODE__ = true
    UIStore.setPwaMode(true)
  }

  setDocumentTitle()
}

// Watchers
watchSyncEffect(() => {
  if (!isAppReady.value) {
    return
  }

  void route.fullPath
  void locale.value
  pwaDisplayModeMatches.forEach((match) => match.value)
  syncPwaDisplayModeTitle()
})

useEventListener(window, 'pageshow', syncPwaDisplayModeTitle)
useEventListener(window, 'focus', syncPwaDisplayModeTitle)
useEventListener(document, 'visibilitychange', syncPwaDisplayModeTitle)

watch(
  () => authResponse.value?.user?.locale,
  (newLocale) => {
    if (newLocale) {
      locale.value = newLocale
    }
  },
)

watch(
  () => route.fullPath,
  () => {
    applyThemePrimaryColor()
  },
  { immediate: true },
)

watch(
  resolvedSettings,
  () => {
    applyThemePrimaryColor()
  },
  { deep: true },
)

watch(
  whitelabelBrandingSettings,
  () => {
    applyThemePrimaryColor()
    setDocumentTitle()
  },
  { deep: true },
)

watch(
  authResponse,
  () => {
    if (isPwaMode.value) return
    applyThemePrimaryColor()
  },
  { deep: true },
)

watch(
  meetingSettings,
  () => {
    applyThemePrimaryColor()
  },
  { deep: true },
)

// Lifecycle
onMounted(() => window.__OMNI_PWA_RECOVERY__?.ready())

onBeforeMount(async () => {
  await authStore.resolveWhitelabelConfig()
  applyThemePrimaryColor()
  syncPwaDisplayModeTitle()
  isAppReady.value = true
})
</script>
